/** @jsxImportSource @emotion/react */
import { useState, useCallback, useEffect, useRef } from 'react'
import { Book, X, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react'
import { Box, Typography, useTheme, Button, IconButton, Tooltip } from '@mui/material'
import { css } from '@emotion/react'
import { Theme } from '@mui/material/styles'
import { Outlet, useNavigate } from 'react-router-dom'

const MIN_WIDTH = 400
const DEFAULT_WIDTH = 600
const MAX_WIDTH_PERCENT = 85 // Max 85% of viewport

const panelContainerStyles = (theme: Theme, width: number, isCollapsed: boolean) => css`
  position: fixed;
  top: 64px; /* Below navbar */
  right: 0;
  bottom: 0;
  width: ${isCollapsed ? '48px' : `${width}px`};
  background-color: ${theme.palette.background.paper};
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
  z-index: 1100;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const resizeHandleStyles = (theme: Theme) => css`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background-color 0.2s;

  &:hover,
  &.dragging {
    background-color: ${theme.palette.primary.main}20;
  }

  .grip-icon {
    opacity: 0;
    transition: opacity 0.2s;
    color: ${theme.palette.grey[400]};
  }

  &:hover .grip-icon,
  &.dragging .grip-icon {
    opacity: 1;
    color: ${theme.palette.primary.main};
  }
`

const panelHeaderStyles = (theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: ${theme.palette.grey[50]};
  border-bottom: 1px solid ${theme.palette.grey[200]};
  min-height: 56px;
`

const iconBoxStyles = (theme: Theme) => css`
  background-color: ${theme.palette.primary.main}15;
  height: 32px;
  width: 32px;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const titleStyles = css`
  font-weight: 600;
  font-size: 16px;
  margin-left: 12px;
`

const panelContentStyles = css`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const collapsedHeaderStyles = (_theme: Theme) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px;
  gap: 12px;
`

const confirmOverlayStyles = css`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`

const confirmDialogStyles = css`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  width: 90%;
`

const StoryBuilderPanel = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [showConfirmClose, setShowConfirmClose] = useState(false)
  const [panelWidth, setPanelWidth] = useState(DEFAULT_WIDTH)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartX = useRef(0)
  const dragStartWidth = useRef(0)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setIsDragging(true)
      dragStartX.current = e.clientX
      dragStartWidth.current = panelWidth
    },
    [panelWidth],
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return

      const deltaX = dragStartX.current - e.clientX
      const newWidth = dragStartWidth.current + deltaX
      const maxWidth = window.innerWidth * (MAX_WIDTH_PERCENT / 100)

      setPanelWidth(Math.min(Math.max(newWidth, MIN_WIDTH), maxWidth))
    },
    [isDragging],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'ew-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  const handleClose = () => {
    setShowConfirmClose(true)
  }

  const confirmClose = () => {
    setShowConfirmClose(false)
    navigate('/app/risk/explorer')
  }

  const cancelClose = () => {
    setShowConfirmClose(false)
  }

  const handleDoubleClick = () => {
    // Double-click to toggle between default and max width
    const maxWidth = window.innerWidth * (MAX_WIDTH_PERCENT / 100)
    if (panelWidth < maxWidth - 100) {
      setPanelWidth(maxWidth)
    } else {
      setPanelWidth(DEFAULT_WIDTH)
    }
  }

  if (isCollapsed) {
    return (
      <Box css={panelContainerStyles(theme, 48, true)}>
        <Box css={collapsedHeaderStyles(theme)}>
          <IconButton size="small" onClick={() => setIsCollapsed(false)} sx={{ mb: 1 }}>
            <ChevronLeft size={20} />
          </IconButton>
          <Box css={iconBoxStyles(theme)}>
            <Book size={18} color={theme.palette.primary.main} />
          </Box>
          <Typography
            sx={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              fontSize: 14,
              fontWeight: 600,
              color: theme.palette.text.secondary,
            }}
          >
            Story Builder
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Box css={panelContainerStyles(theme, panelWidth, false)}>
        {/* Resize handle */}
        <Tooltip title="Drag to resize, double-click to maximize" placement="left">
          <Box
            css={resizeHandleStyles(theme)}
            className={isDragging ? 'dragging' : ''}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
          >
            <GripVertical size={12} className="grip-icon" />
          </Box>
        </Tooltip>

        <Box css={panelHeaderStyles(theme)}>
          <Box display="flex" alignItems="center">
            <IconButton size="small" onClick={() => setIsCollapsed(true)} sx={{ mr: 1 }}>
              <ChevronRight size={20} />
            </IconButton>
            <Box css={iconBoxStyles(theme)}>
              <Book size={18} color={theme.palette.primary.main} />
            </Box>
            <Typography css={titleStyles}>Story Builder</Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.error.light + '20',
                color: theme.palette.error.main,
              },
            }}
          >
            <X size={20} />
          </IconButton>
        </Box>
        <Box css={panelContentStyles}>
          <Outlet />
        </Box>
      </Box>

      {showConfirmClose && (
        <Box
          css={confirmOverlayStyles}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              cancelClose()
            }
          }}
        >
          <Box css={confirmDialogStyles} onClick={(e) => e.stopPropagation()}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Discard changes?
            </Typography>
            <Typography sx={{ mb: 3, color: 'text.secondary' }}>
              You have unsaved changes. Are you sure you want to close the story builder?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={cancelClose}>
                Keep Editing
              </Button>
              <Button variant="contained" color="error" onClick={confirmClose}>
                Discard
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  )
}

export default StoryBuilderPanel
